import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Turnstile } from "@marsidev/react-turnstile";
import { Wine, Mail, Lock, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

// Cloudflare Turnstile sitekey.
// Reads from env first; falls back to the production sitekey hardcoded here.
// The fallback exists because the Emergent production deploy pipeline has
// dropped REACT_APP_* env vars on us before — hardcoding the real production
// sitekey ensures the live app always boots with a real key, not a test key.
// (Turnstile sitekeys are public by design — they're rendered into the HTML
// for every visitor — so there is no secret being leaked here.)
const SITEKEY = process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITEKEY || "0x4AAAAAADUi2_DcnAYkZ6mD";

export default function Login() {
  const { login, demoLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileKey, setTurnstileKey] = useState(0); // reset widget after submit
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!turnstileToken) {
      toast.error("Please complete the bot check.");
      return;
    }
    setBusy(true);
    const r = await login(email, password, turnstileToken);
    setBusy(false);
    if (!r.ok) {
      setTurnstileToken("");
      setTurnstileKey((k) => k + 1);
      toast.error(r.error || "Authentication failed");
      return;
    }
    if (r.mfaSetupRequired) {
      toast.message("One more step — set up two-factor authentication.");
      navigate("/mfa/setup");
      return;
    }
    if (r.mfaRequired) {
      toast.message("Enter your authenticator code.");
      navigate("/mfa/verify");
      return;
    }
    toast.success(`Welcome back${r.user?.name ? ", " + r.user.name : ""}`);
    navigate("/");
  };

  const onDemo = async () => {
    setBusy(true);
    const r = await demoLogin();
    setBusy(false);
    if (r.ok) {
      toast.success("Demo mode — explore freely");
      navigate("/");
    } else {
      toast.error(r.error || "Demo unavailable");
    }
  };

  return (
    <div className="min-h-screen relative login-canvas grain overflow-hidden">
      <div className="relative z-10 max-w-[1200px] mx-auto px-4 sm:px-8 py-8 min-h-screen grid md:grid-cols-2 gap-10 items-center">
        <div className="hidden md:flex flex-col gap-8 fade-in">
          <div className="flex items-center gap-3">
            <img
              src="/literally-logo.png"
              alt="Literally Vodka"
              className="w-12 h-12 rounded-xl object-cover shadow-soft"
            />
            <div>
              <div className="font-display text-lg text-brand-ink">LiterallyDrink</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-brand-mutedFg">Sales Tracker</div>
            </div>
          </div>

          <div>
            <div className="label mb-4">The Literally Spirits sales floor</div>
            <h1 className="font-display text-5xl lg:text-6xl leading-[1.05] text-brand-ink">
              Pour <span className="italic text-brand-gold">Literally Vodka</span> behind every bar.
            </h1>
            <p className="text-brand-mutedFg mt-5 max-w-md text-base">
              The internal sales tracker for the Literally Spirits team — discover nearby
              restaurants, bars, and liquor stores, move every account through your pipeline,
              and log every visit on the road. Crisp, clean, smooth. Like the vodka.
            </p>
          </div>

          {/* Logo showcase tile */}
          <div className="card p-4 max-w-md inline-flex items-center gap-4">
            <img
              src="/literally-logo.png"
              alt="Literally Vodka label"
              className="w-20 h-20 rounded-lg object-cover"
            />
            <div>
              <div className="label">Now pouring</div>
              <div className="font-display text-xl text-brand-ink leading-tight">Literally Vodka</div>
              <div className="text-xs text-brand-mutedFg mt-1">Made in Georgia · 80 proof</div>
            </div>
          </div>
        </div>

        <div className="w-full max-w-md mx-auto md:ml-auto fade-in">
          <div className="card p-8 sm:p-10">
            <div className="flex md:hidden items-center gap-3 mb-6">
              <img
                src="/literally-logo.png"
                alt="Literally Vodka"
                className="w-10 h-10 rounded-lg object-cover"
              />
              <div className="font-display text-lg text-brand-ink">LiterallyDrink</div>
            </div>

            <div className="label">Welcome back</div>
            <h2 className="font-display text-3xl text-brand-ink mt-1">Sign in to Literally Spirits</h2>

            <form onSubmit={submit} className="mt-6 flex flex-col gap-3" data-testid="auth-form">
              <label className="block">
                <span className="label">Email</span>
                <div className="relative mt-1">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-mutedFg" />
                  <input
                    data-testid="auth-email-input"
                    type="email"
                    className="input-base pl-10"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@literallydrink.com"
                    autoComplete="email"
                  />
                </div>
              </label>

              <label className="block">
                <span className="label">Password</span>
                <div className="relative mt-1">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-mutedFg" />
                  <input
                    data-testid="auth-password-input"
                    type="password"
                    className="input-base pl-10"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters incl. a number"
                    autoComplete="current-password"
                  />
                </div>
              </label>

              <div className="mt-2 flex justify-center" data-testid="turnstile-wrap">
                {SITEKEY ? (
                  <Turnstile
                    key={turnstileKey}
                    siteKey={SITEKEY}
                    onSuccess={setTurnstileToken}
                    onError={() => setTurnstileToken("")}
                    onExpire={() => setTurnstileToken("")}
                    options={{ theme: "light", size: "flexible" }}
                  />
                ) : (
                  <div className="text-xs text-stage-lostFg">Bot check is not configured.</div>
                )}
              </div>

              <button
                data-testid="auth-submit-button"
                type="submit"
                disabled={busy || !turnstileToken}
                className="btn-primary mt-2 group"
              >
                {busy ? (<><Loader2 size={16} className="animate-spin" /> Working…</>) : (
                  <>Sign in <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" /></>
                )}
              </button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-brand-border"></div></div>
              <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-brand-mutedFg uppercase tracking-widest">or</span></div>
            </div>

            <button
              data-testid="demo-login-button"
              type="button"
              onClick={onDemo}
              disabled={busy}
              className="btn-secondary w-full"
            >
              <Sparkles size={16} />
              Try Demo Mode
            </button>

            <div className="text-center text-xs text-brand-mutedFg mt-6">
              The Literally Spirits sales tracker is <strong className="text-brand-ink">invite-only</strong>.<br />
              Need access? Reach out to your Literally Spirits sales lead.
            </div>
          </div>
          <p className="text-center text-xs text-brand-mutedFg mt-4">
            Literally Vodka · Crafted in Georgia · Drink responsibly · 21+
          </p>
        </div>
      </div>
    </div>
  );
}
